import type { CalidadControlModificacion } from "../../../types/control-modificaciones";

export const calidadShortMarkOptions = ["", "X", "(X)", "NA"] as const;

export type CalidadShortMark = (typeof calidadShortMarkOptions)[number];
export type CalidadSuministradorKind = "indirecto" | "directo" | "grupo";

type CalidadBaseRecord = {
  id: number;
  nombre: string;
  usuario_modificacion_id: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export type CalidadSuministradorHomologadoIndirecto = CalidadBaseRecord & {
  material_servicio_m: string | null;
  material_servicio_t: string | null;
  material_servicio_g: string | null;
  material_servicio_a: string | null;
  tipologia_dir: string | null;
  tipologia_ind: string | null;
  tipologia_pun: string | null;
  tipologia_con: string | null;
  tipologia_nac: string | null;
  tipologia_loc: string | null;
  autorizacion: string | null;
  uso_web_plan: string | null;
  uso_web_doc: string | null;
  nivel_confianza_ca: string | null;
  nivel_confianza_cm: string | null;
  nivel_confianza_cb: string | null;
  nivel_confianza_pc: string | null;
  nivel_confianza_nc: string | null;
  observaciones_interes: string | null;
};

export type CalidadSuministradorHomologadoDirecto = CalidadBaseRecord & {
  servicio: string | null;
  comentarios: string | null;
  nivel_confianza_ca: string | null;
  nivel_confianza_cm: string | null;
  nivel_confianza_cb: string | null;
  nivel_confianza_pc: string | null;
  nivel_confianza_nc: string | null;
  observaciones_interes: string | null;
};

export type CalidadSuministradorHomologadoGrupo = CalidadBaseRecord & {
  servicio: string | null;
  comentarios: string | null;
  nivel_confianza_ca: string | null;
  nivel_confianza_cm: string | null;
  nivel_confianza_cb: string | null;
  nivel_confianza_pc: string | null;
  nivel_confianza_nc: string | null;
  observaciones_interes: string | null;
};

export type CalidadSuministradoresHomologadosData = {
  indirectos: CalidadSuministradorHomologadoIndirecto[];
  directos: CalidadSuministradorHomologadoDirecto[];
  grupo: CalidadSuministradorHomologadoGrupo[];
};

export type CalidadSuministradoresHomologadosResponse = {
  data: CalidadSuministradoresHomologadosData;
  control_modificaciones: CalidadControlModificacion[];
  meta: {
    counts: {
      indirectos: number;
      directos: number;
      grupo: number;
      control_modificaciones?: number;
    };
  };
};

export type CalidadIndirectoPayload = {
  nombre: string;
  material_servicio_m: string | null;
  material_servicio_t: string | null;
  material_servicio_g: string | null;
  material_servicio_a: string | null;
  tipologia_dir: string | null;
  tipologia_ind: string | null;
  tipologia_pun: string | null;
  tipologia_con: string | null;
  tipologia_nac: string | null;
  tipologia_loc: string | null;
  autorizacion: string | null;
  uso_web_plan: string | null;
  uso_web_doc: string | null;
  nivel_confianza_ca: string | null;
  nivel_confianza_cm: string | null;
  nivel_confianza_cb: string | null;
  nivel_confianza_pc: string | null;
  nivel_confianza_nc: string | null;
  observaciones_interes: string | null;
};

export type CalidadDirectLikePayload = {
  nombre: string;
  servicio: string | null;
  comentarios: string | null;
  nivel_confianza_ca: string | null;
  nivel_confianza_cm: string | null;
  nivel_confianza_cb: string | null;
  nivel_confianza_pc: string | null;
  nivel_confianza_nc: string | null;
  observaciones_interes: string | null;
};

export function createEmptyIndirectoForm(): CalidadIndirectoPayload {
  return {
    nombre: "",
    material_servicio_m: "",
    material_servicio_t: "",
    material_servicio_g: "",
    material_servicio_a: "",
    tipologia_dir: "",
    tipologia_ind: "",
    tipologia_pun: "",
    tipologia_con: "",
    tipologia_nac: "",
    tipologia_loc: "",
    autorizacion: "",
    uso_web_plan: "",
    uso_web_doc: "",
    nivel_confianza_ca: "",
    nivel_confianza_cm: "",
    nivel_confianza_cb: "",
    nivel_confianza_pc: "",
    nivel_confianza_nc: "",
    observaciones_interes: "",
  };
}

export function createEmptyDirectLikeForm(): CalidadDirectLikePayload {
  return {
    nombre: "",
    servicio: "",
    comentarios: "",
    nivel_confianza_ca: "",
    nivel_confianza_cm: "",
    nivel_confianza_cb: "",
    nivel_confianza_pc: "",
    nivel_confianza_nc: "",
    observaciones_interes: "",
  };
}

export function mapIndirectoToPayload(
  record: CalidadSuministradorHomologadoIndirecto,
): CalidadIndirectoPayload {
  return {
    nombre: record.nombre,
    material_servicio_m: record.material_servicio_m ?? "",
    material_servicio_t: record.material_servicio_t ?? "",
    material_servicio_g: record.material_servicio_g ?? "",
    material_servicio_a: record.material_servicio_a ?? "",
    tipologia_dir: record.tipologia_dir ?? "",
    tipologia_ind: record.tipologia_ind ?? "",
    tipologia_pun: record.tipologia_pun ?? "",
    tipologia_con: record.tipologia_con ?? "",
    tipologia_nac: record.tipologia_nac ?? "",
    tipologia_loc: record.tipologia_loc ?? "",
    autorizacion: record.autorizacion ?? "",
    uso_web_plan: record.uso_web_plan ?? "",
    uso_web_doc: record.uso_web_doc ?? "",
    nivel_confianza_ca: record.nivel_confianza_ca ?? "",
    nivel_confianza_cm: record.nivel_confianza_cm ?? "",
    nivel_confianza_cb: record.nivel_confianza_cb ?? "",
    nivel_confianza_pc: record.nivel_confianza_pc ?? "",
    nivel_confianza_nc: record.nivel_confianza_nc ?? "",
    observaciones_interes: record.observaciones_interes ?? "",
  };
}

export function mapDirectoToPayload(
  record: CalidadSuministradorHomologadoDirecto,
): CalidadDirectLikePayload {
  return {
    nombre: record.nombre,
    servicio: record.servicio ?? "",
    comentarios: record.comentarios ?? "",
    nivel_confianza_ca: record.nivel_confianza_ca ?? "",
    nivel_confianza_cm: record.nivel_confianza_cm ?? "",
    nivel_confianza_cb: record.nivel_confianza_cb ?? "",
    nivel_confianza_pc: record.nivel_confianza_pc ?? "",
    nivel_confianza_nc: record.nivel_confianza_nc ?? "",
    observaciones_interes: record.observaciones_interes ?? "",
  };
}

export function mapGrupoToPayload(
  record: CalidadSuministradorHomologadoGrupo,
): CalidadDirectLikePayload {
  return {
    nombre: record.nombre,
    servicio: record.servicio ?? "",
    comentarios: record.comentarios ?? "",
    nivel_confianza_ca: record.nivel_confianza_ca ?? "",
    nivel_confianza_cm: record.nivel_confianza_cm ?? "",
    nivel_confianza_cb: record.nivel_confianza_cb ?? "",
    nivel_confianza_pc: record.nivel_confianza_pc ?? "",
    nivel_confianza_nc: record.nivel_confianza_nc ?? "",
    observaciones_interes: record.observaciones_interes ?? "",
  };
}

export function normalizeNullableMark(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();

  return trimmed === "" ? null : trimmed;
}

export function mapIndirectoFormToPayload(
  form: CalidadIndirectoPayload,
): CalidadIndirectoPayload {
  return {
    nombre: form.nombre.trim(),
    material_servicio_m: normalizeNullableMark(form.material_servicio_m),
    material_servicio_t: normalizeNullableMark(form.material_servicio_t),
    material_servicio_g: normalizeNullableMark(form.material_servicio_g),
    material_servicio_a: normalizeNullableMark(form.material_servicio_a),
    tipologia_dir: normalizeNullableMark(form.tipologia_dir),
    tipologia_ind: normalizeNullableMark(form.tipologia_ind),
    tipologia_pun: normalizeNullableMark(form.tipologia_pun),
    tipologia_con: normalizeNullableMark(form.tipologia_con),
    tipologia_nac: normalizeNullableMark(form.tipologia_nac),
    tipologia_loc: normalizeNullableMark(form.tipologia_loc),
    autorizacion: normalizeNullableMark(form.autorizacion),
    uso_web_plan: normalizeNullableMark(form.uso_web_plan),
    uso_web_doc: normalizeNullableMark(form.uso_web_doc),
    nivel_confianza_ca: normalizeNullableMark(form.nivel_confianza_ca),
    nivel_confianza_cm: normalizeNullableMark(form.nivel_confianza_cm),
    nivel_confianza_cb: normalizeNullableMark(form.nivel_confianza_cb),
    nivel_confianza_pc: normalizeNullableMark(form.nivel_confianza_pc),
    nivel_confianza_nc: normalizeNullableMark(form.nivel_confianza_nc),
    observaciones_interes: normalizeNullableMark(form.observaciones_interes),
  };
}

export function mapDirectLikeFormToPayload(
  form: CalidadDirectLikePayload,
): CalidadDirectLikePayload {
  return {
    nombre: form.nombre.trim(),
    servicio: normalizeNullableMark(form.servicio),
    comentarios: normalizeNullableMark(form.comentarios),
    nivel_confianza_ca: normalizeNullableMark(form.nivel_confianza_ca),
    nivel_confianza_cm: normalizeNullableMark(form.nivel_confianza_cm),
    nivel_confianza_cb: normalizeNullableMark(form.nivel_confianza_cb),
    nivel_confianza_pc: normalizeNullableMark(form.nivel_confianza_pc),
    nivel_confianza_nc: normalizeNullableMark(form.nivel_confianza_nc),
    observaciones_interes: normalizeNullableMark(form.observaciones_interes),
  };
}
